import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Loader2, CheckCircle, XCircle, Clock, CreditCard, Sparkles, Zap } from "lucide-react";

// Using relative URL for API calls
const API = "/api";

export default function PaymentHistoryModal({ open, onOpenChange }) {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    if (open) {
      fetchPaymentHistory();
    }
  }, [open]);

  const fetchPaymentHistory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API}/payments/history`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });

      if (response.ok) {
        const data = await response.json();
        setTransactions(data.transactions);
      }
    } catch (error) {
      console.error("Error fetching payment history:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "paid":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "pending":
      case "initiated":
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case "failed":
      case "expired":
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "paid":
        return "Completed";
      case "pending":
      case "initiated":
        return "Pending";
      case "failed":
        return "Failed";
      case "expired":
        return "Expired";
      default:
        return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "text-green-500";
      case "pending":
      case "initiated":
        return "text-yellow-500";
      case "failed":
      case "expired":
        return "text-red-500";
      default:
        return "text-gray-500";
    }
  };

  const getPlanIcon = (planId) => {
    if (planId === "pro") {
      return <Zap className="w-4 h-4 text-[#14A800]" />;
    }
    return <Sparkles className="w-4 h-4 text-[#14A800]" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatAmount = (amount, currency) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(amount);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1E1E1E] border-[#333] text-white max-w-2xl max-h-[80vh] overflow-hidden" data-testid="payment-history-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#14A800]" />
            Payment History
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 overflow-y-auto max-h-[60vh] pr-2">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#14A800] mb-4" />
              <p className="text-[#888]">Loading payment history...</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <CreditCard className="w-12 h-12 text-[#333] mb-4" />
              <p className="text-[#888] text-center">No payment history yet</p>
              <p className="text-[#666] text-sm text-center mt-2">
                Your transactions will appear here after you make a purchase.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {transactions.map((txn, index) => (
                <div
                  key={txn.transaction_id || index}
                  className="bg-[#0D0D0D] border border-[#333] rounded-lg p-4 hover:border-[#444] transition-colors"
                  data-testid={`transaction-${index}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-[#1E1E1E]">
                        {getPlanIcon(txn.plan_id)}
                      </div>
                      <div>
                        <h4 className="font-medium text-white">{txn.plan_name}</h4>
                        <p className="text-xs text-[#666] mt-1">
                          {formatDate(txn.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-white">
                        {formatAmount(txn.amount, txn.currency)}
                      </p>
                      <div className={`flex items-center gap-1.5 justify-end mt-1 ${getStatusColor(txn.payment_status)}`}>
                        {getStatusIcon(txn.payment_status)}
                        <span className="text-xs font-medium">
                          {getStatusText(txn.payment_status)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Transaction ID */}
                  <div className="mt-3 pt-3 border-t border-[#222]">
                    <p className="text-xs text-[#555]">
                      Transaction ID: <span className="text-[#777] font-mono">{txn.transaction_id}</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-[#333]">
          <p className="text-xs text-[#666] text-center">
            For billing questions, contact support@unizar.com
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
